import { DEFAULT_PAGE_SIZE } from '../constants/pagination'
import { ApiResponse, WorkflowStatus } from '../types'
import { apiService } from './api.service'

class WorkflowStatusService {
  private _listAllCache: Promise<ApiResponse<WorkflowStatus>> | null = null

  async getWorkflowStatuses(params?: { page?: number; page_size?: number }): Promise<ApiResponse<WorkflowStatus>> {
    const response = await apiService.get<ApiResponse<WorkflowStatus>>('workflow_statuses/', { params })
    return response.data
  }

  async listAll(): Promise<ApiResponse<WorkflowStatus>> {
    if (!this._listAllCache) {
      this._listAllCache = apiService
        .get<ApiResponse<WorkflowStatus>>('workflow_statuses/', {
          params: { page: 1, page_size: 100000 },
        })
        .then((response) => response.data)
    }
    return this._listAllCache
  }

  clearCache(): void {
    this._listAllCache = null
  }

  async getWorkflowStatus(id: number): Promise<WorkflowStatus> {
    const response = await apiService.get<WorkflowStatus>(`workflow_statuses/${id}/`)
    return response.data
  }

  async createWorkflowStatus(data: Partial<WorkflowStatus>): Promise<WorkflowStatus> {
    this.clearCache()
    const response = await apiService.post<WorkflowStatus>('workflow_statuses/', data)
    return response.data
  }

  async updateWorkflowStatus(id: number, data: Partial<WorkflowStatus>): Promise<WorkflowStatus> {
    this.clearCache()
    const response = await apiService.patch<WorkflowStatus>(`workflow_statuses/${id}/`, data)
    return response.data
  }

  async deleteWorkflowStatus(id: number): Promise<void> {
    this.clearCache()
    await apiService.delete(`workflow_statuses/${id}/`)
  }

  async listFiltered(
    page: number,
    _ordering?: string,
    sortField?: string,
    sortReverse?: boolean,
    nameFilter?: string,
    _includeDocumentCount?: boolean,
    extraParams?: Record<string, unknown>
  ): Promise<ApiResponse<WorkflowStatus>> {
    const params: Record<string, unknown> = {
      page,
      page_size: extraParams?.page_size ?? DEFAULT_PAGE_SIZE,
      full_perms: true,
    }

    if (sortField) {
      params.ordering = (sortReverse ? '-' : '') + sortField
    }

    if (nameFilter) {
      params.name__icontains = nameFilter
    }

    if (extraParams) {
      Object.assign(params, extraParams)
    }

    return this.getWorkflowStatuses(params as { page?: number; page_size?: number })
  }

  async delete(status: WorkflowStatus): Promise<void> {
    return this.deleteWorkflowStatus(status.id)
  }

  async create(data: Partial<WorkflowStatus>): Promise<WorkflowStatus> {
    return this.createWorkflowStatus(data)
  }

  async update(id: number, data: Partial<WorkflowStatus>): Promise<WorkflowStatus> {
    return this.updateWorkflowStatus(id, data)
  }

  async bulkEditObjects(objects: number[], operation: string, permissions?: any, merge?: boolean): Promise<void> {
    const params: Record<string, unknown> = {
      objects,
      object_type: 'workflow_statuses',
      operation,
    }

    if (operation === 'set_permissions' && permissions) {
      params.owner = permissions.owner
      params.permissions = permissions.set_permissions
      params.merge = merge
    }

    await apiService.post('bulk_edit_objects/', params)
  }
}

export const workflowStatusService = new WorkflowStatusService()
